import { useEffect, useState } from "react";
import type { AttendeeAccountDeletionPreview, AttendeeAccountDeletionResult } from "@verity-connect/contracts";
import { deletePrivateNotes } from "./private-notes";
import { emitAttendeeStateChanged } from "./cross-device-state";

/**
 * Deleting the account she made.
 *
 * Apple will not take an application that lets somebody create an account and gives them
 * no way to remove it, which is why this exists. It is also simply right: she signed up to
 * see a schedule, and leaving should not require an email to an association.
 *
 * The words come from the server rather than from here. What is deleted depends on what
 * else she is — a teacher who also registers her school keeps her sign-in, because
 * removing it would take her school's access with it — and a screen that guessed at its
 * own wording would eventually promise something the server does not do.
 *
 * Two taps, not one, and the second one says what it does rather than "OK". Nothing here
 * is recoverable afterwards.
 */
export function DeleteAccountPanel({ eventSlug, identityId, onDeleted }: {
  eventSlug: string;
  identityId: string;
  onDeleted: (signedOut: boolean) => void;
}) {
  const [preview, setPreview] = useState<AttendeeAccountDeletionPreview | null>(null);
  const [asking, setAsking] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const address = `/api/attendee/events/${encodeURIComponent(eventSlug)}/account/deletion`;

  // Fetched when she opens the panel rather than on every visit to the page, so the
  // ordinary case costs nothing.
  useEffect(() => {
    if (!asking || preview) return;
    let cancelled = false;
    void fetch(address, { credentials: "same-origin" })
      .then((response) => (response.ok ? response.json() as Promise<AttendeeAccountDeletionPreview> : null))
      .then((value) => { if (!cancelled && value) setPreview(value); })
      .catch(() => { if (!cancelled) setError("This could not be opened. Try again in a moment."); });
    return () => { cancelled = true; };
  }, [asking, preview, address]);

  async function remove() {
    setWorking(true);
    setError(null);
    let deleted = false;
    try {
      const response = await fetch(address, { method: "DELETE", credentials: "same-origin" });
      if (!response.ok) throw new Error("The account could not be deleted.");
      const result = await response.json() as AttendeeAccountDeletionResult;
      deleted = true;
      let deviceCleanupFailed = false;
      try { await deletePrivateNotes(identityId); } catch { deviceCleanupFailed = true; }
      if (deviceCleanupFailed) {
        window.alert("Your account was deleted, but this device could not clear its saved note copies. Clear WVCEA Connect's site data before another person uses this device.");
      }
      emitAttendeeStateChanged("account-deleted");
      onDeleted(result.signedOut);
    } catch {
      setError(deleted ? "Your account was deleted, but this device could not clear its saved note copies." : "The account could not be deleted. Nothing was removed — try again in a moment.");
      setWorking(false);
    }
  }

  if (!asking) {
    return (
      <button className="attendee-delete-open" type="button" onClick={() => setAsking(true)}>
        Delete your account
      </button>
    );
  }

  return (
    <section className="attendee-delete-panel" aria-labelledby="delete-account-title">
      <h2 id="delete-account-title">Delete your account</h2>
      {preview
        ? preview.sentences.map((sentence) => <p key={sentence}>{sentence}</p>)
        : <p className="plain-state">Checking what this will remove…</p>}
      {error && <p className="form-error" role="alert">{error}</p>}
      <div className="attendee-delete-actions">
        <button
          className="attendee-delete-confirm"
          type="button"
          disabled={!preview || working}
          onClick={() => void remove()}
        >
          {working ? "Deleting…" : "Delete my account"}
        </button>
        <button className="attendee-inline-button" type="button" disabled={working} onClick={() => setAsking(false)}>
          Keep my account
        </button>
      </div>
    </section>
  );
}
